import DatasetIcon from "@mui/icons-material/Dataset";
import FilterHdrIcon from "@mui/icons-material/FilterHdr";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { Box, ButtonBase, Paper, Typography } from "@mui/material";
import { useNavBarTitle } from "components/NavBar";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Role from "constants/roles";
import { useRoleAnywhere } from "store/session";
import t from "./translations";

type Tool = {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
};

const PANEL_SIZE = 240;
const GRID_COLUMNS = 4;
const GRID_GAP = 24;
const GRID_MAX_WIDTH =
  GRID_COLUMNS * PANEL_SIZE + (GRID_COLUMNS - 1) * GRID_GAP;

export default function Splash() {
  const navigate = useNavigate();
  const { hasRole } = useRoleAnywhere();
  const isGlobalAdmin = hasRole(Role.GlobalAdmin);
  const isEmployee = hasRole(Role.SkyqraftEmployee);

  useNavBarTitle(t.title);

  const tools = useMemo<Tool[]>(
    () => [
      {
        key: "datasets",
        label: t.tools.datasets,
        path: "/datasets",
        icon: <DatasetIcon sx={{ fontSize: 64 }} />,
      },
      {
        key: "objectTypeMerge",
        label: t.tools.objectTypeMerge,
        path: "/admin/object-type-merge",
        icon: <MergeTypeIcon sx={{ fontSize: 64 }} />,
        disabled: !isGlobalAdmin,
      },
      {
        key: "lidarClearance",
        label: t.tools.lidarClearance,
        path: "/admin/lidar-clearance",
        icon: <FilterHdrIcon sx={{ fontSize: 64 }} />,
        disabled: !isEmployee,
      },
    ],
    [isGlobalAdmin, isEmployee]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 4,
      }}
      data-testid="splash-page"
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: `${GRID_GAP}px`,
          maxWidth: GRID_MAX_WIDTH,
        }}
      >
        {tools.map((tool) => (
          <ButtonBase
            key={tool.key}
            disabled={tool.disabled}
            onClick={() => navigate(tool.path)}
            data-testid={`tool-panel-${tool.key}`}
            sx={{ borderRadius: 2 }}
          >
            <Paper
              elevation={2}
              sx={{
                width: PANEL_SIZE,
                height: PANEL_SIZE,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                opacity: tool.disabled ? 0.5 : 1,
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": tool.disabled
                  ? undefined
                  : {
                      boxShadow: 6,
                      transform: "translateY(-2px)",
                    },
              }}
            >
              {tool.icon}
              <Typography variant="h6" component="span">
                {tool.label}
              </Typography>
            </Paper>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
